import { AddCircleOutline, CheckCircleRounded } from '@mui/icons-material';
import { IconButton, Stack, TableBody, TableRow, Tooltip, Typography } from '@mui/material';
import { TagTableCell } from './styles';
import { SupplierItemWithAssortmentTags } from '@retail/products/types';
import { AssortmentTag } from '@retail/my-assortment/types';
import { useState } from 'react';
import { AssortmentTagTableCell } from './AssortmentTagTableCell';
import { useProductsTFunction } from '@retail/products/i18n';
import { getLLDate } from '@retail/utils';

export interface AssortmentTagWithSupplierItemId {
  assortmentTag: AssortmentTag;
  mgSupplierItemId: number;
}

interface Props {
  supplierItemsTags: SupplierItemWithAssortmentTags[];
  assortmentTags: AssortmentTag[];
  readOnly?: boolean;
  setAssortmentTagToAdd?: (tags: AssortmentTagWithSupplierItemId) => void;
  setAssortmentTagToEdit?: (tag: AssortmentTagWithSupplierItemId) => void;
  setItemAssortmentTagToDelete?: (tag: AssortmentTag, mgSupplierItemId: number) => void;
  setSupplierItemToAddToAssortment?: (supplierItem: SupplierItemWithAssortmentTags) => void;
}

export function AssortmentTagTableContent({
  supplierItemsTags,
  assortmentTags,
  readOnly,
  setAssortmentTagToAdd,
  setAssortmentTagToEdit,
  setItemAssortmentTagToDelete,
  setSupplierItemToAddToAssortment,
}: Props) {
  const t = useProductsTFunction();
  const [hoveredCell, setHoveredCell] = useState<AssortmentTagWithSupplierItemId | undefined>(
    undefined
  );
  const [hoveredAssortmentCell, setHoveredAssortmentCell] = useState<number | undefined>(undefined);
  return (
    <TableBody>
      <TableRow
        sx={(theme) => ({ bgcolor: `rgba(${theme.vars.palette.success.mainChannel} / 0.08)` })}
      >
        <TagTableCell>
          <Typography variant="subtitle2">{t('products.mgItem.assortment')}</Typography>
        </TagTableCell>
        {supplierItemsTags.map((supplierItemsTags) => (
          <TagTableCell
            key={supplierItemsTags.supplier.supplierNumber}
            align="center"
            interactive={!readOnly && !supplierItemsTags.inAssortment}
            onMouseEnter={() =>
              !readOnly && setHoveredAssortmentCell(supplierItemsTags.mgSupplierItemId)
            }
            onMouseLeave={() => setHoveredAssortmentCell(undefined)}
          >
            {supplierItemsTags.inAssortment ? (
              <Stack alignItems="center" gap={0.5}>
                <CheckCircleRounded fontSize="small" sx={{ color: 'success.main' }} />
                <Typography variant="body3" color="text.secondary">
                  {getLLDate(supplierItemsTags.inAssortmentFrom)}
                </Typography>
                <Typography variant="body3" color="text.secondary">
                  {getLLDate(supplierItemsTags.inAssortmentTo)}
                </Typography>
              </Stack>
            ) : hoveredAssortmentCell === supplierItemsTags.mgSupplierItemId ? (
              <Tooltip title={t('products.mgItem.addItemsToAssortment.addToAssortment')} arrow>
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={() => setSupplierItemToAddToAssortment?.(supplierItemsTags)}
                >
                  <AddCircleOutline fontSize="small" sx={{ color: 'success.main' }} />
                </IconButton>
              </Tooltip>
            ) : (
              '-'
            )}
          </TagTableCell>
        ))}
      </TableRow>

      {assortmentTags.map((tag) => {
        return (
          <TableRow key={tag.id}>
            <TagTableCell>
              <Typography variant="body1">{tag.name}</Typography>
            </TagTableCell>

            {supplierItemsTags.map((supplierItemsTags) => (
              <TagTableCell
                key={supplierItemsTags.supplier.supplierNumber}
                align="center"
                interactive={!readOnly}
                sx={{ p: 0 }}
                onMouseEnter={() =>
                  !readOnly &&
                  setHoveredCell({
                    assortmentTag: tag,
                    mgSupplierItemId: supplierItemsTags.mgSupplierItemId,
                  })
                }
                onMouseLeave={() => setHoveredCell(undefined)}
              >
                <AssortmentTagTableCell
                  currentTag={tag}
                  supplierItemTags={supplierItemsTags}
                  hoveredCell={hoveredCell}
                  setAssortmentTagToAdd={setAssortmentTagToAdd}
                  setAssortmentTagToEdit={setAssortmentTagToEdit}
                  setItemAssortmentTagToDelete={setItemAssortmentTagToDelete}
                />
              </TagTableCell>
            ))}
          </TableRow>
        );
      })}
    </TableBody>
  );
}
